import {
  Component,
  computed,
  effect,
  HostListener,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { Router } from '@angular/router';
import { CreditosInsuficientesDialogService } from './creditos-insuficientes-dialog.service';
import { CreditosService } from '../../core/services/creditos.service';
import { Servico, ServicosService } from '../../core/services/servicos.service';
import { CheckoutService } from '../../core/services/checkout.service';
import { AppConfigService } from '../../core/config/app-config.service';
import { ApiError } from '../api-error';
import { TranslatePipe } from '../pipes/translate.pipe';
import { I18nService } from '../../core/services/i18n.service';
import { WyLoaderComponent } from '../loader/loader';

@Component({
  selector: 'app-creditos-insuficientes-dialog',
  standalone: true,
  imports: [DecimalPipe, TranslatePipe, WyLoaderComponent],
  templateUrl: './creditos-insuficientes-dialog.html',
})
export class CreditosInsuficientesDialogComponent {
  protected readonly dialog = inject(CreditosInsuficientesDialogService);
  private readonly router = inject(Router);
  private readonly creditos = inject(CreditosService);
  private readonly servicos = inject(ServicosService);
  private readonly checkout = inject(CheckoutService);
  private readonly appConfig = inject(AppConfigService);
  private readonly i18n = inject(I18nService);

  private readonly pacotes = signal<Servico[]>([]);
  private readonly custos = signal<Servico[]>([]);
  protected readonly carregando = signal(false);
  protected readonly comprando = signal(false);
  protected readonly erroCompra = signal('');

  protected readonly saldo = computed(() => this.creditos.saldo()?.totalDisponivel ?? 0);

  /** Custo da operação que falhou, quando o chamador informou qual era. */
  protected readonly custo = computed<number | null>(() => {
    const nome = this.dialog.contexto().servicoNome;
    if (!nome) return null;
    return this.custos().find((s) => s.nome === nome)?.debitoToken ?? null;
  });

  protected readonly faltam = computed<number | null>(() => {
    const custo = this.custo();
    if (custo === null) return null;
    return Math.max(0, custo - this.saldo());
  });

  /**
   * Menor pacote que cobre o que falta — a compra de 1 clique não deve empurrar mais do que o
   * necessário. Sem custo conhecido, recomenda o menor pacote da faixa intermediária, que é a
   * escolha razoável quando não se sabe o tamanho da necessidade.
   */
  protected readonly recomendado = computed<Servico | null>(() => {
    const ordenados = [...this.pacotes()].sort((a, b) => a.valor - b.valor);
    if (ordenados.length === 0) return null;
    const faltam = this.faltam();
    if (faltam === null) {
      return ordenados.find((p) => p.valor > 30) ?? ordenados[0];
    }
    return ordenados.find((p) => p.creditoToken >= faltam) ?? ordenados[ordenados.length - 1];
  });

  constructor() {
    // Carrega saldo e catálogo só quando o modal abre: até aí é um componente inerte no app.
    // `untracked` é obrigatório aqui: `carregar()` lê e escreve signals no trecho síncrono, e
    // sem isso o efeito reagiria à própria carga, recarregando em loop.
    effect(() => {
      if (this.dialog.open()) untracked(() => void this.carregar());
    });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.dialog.open()) {
      this.close();
    }
  }

  protected close(): void {
    this.dialog.fechar();
    this.erroCompra.set('');
  }

  protected onOverlayClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).dataset['overlay']) {
      this.close();
    }
  }

  protected formatBRL(valor: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
  }

  /** Compra o pacote recomendado sem passar pela lista: um clique a menos no caminho do dinheiro. */
  protected async comprarRecomendado(): Promise<void> {
    const pacote = this.recomendado();
    if (!pacote || this.comprando()) return;
    this.comprando.set(true);
    this.erroCompra.set('');
    // Volta para a tela em que a pessoa estava: ela interrompeu uma criação para recarregar.
    const successUrl = `${this.appConfig.config.siteUrl}${this.router.url}`;
    try {
      const resultado = await this.checkout.realizar(pacote.id, successUrl, crypto.randomUUID());
      window.location.href = resultado.invoiceUrl ?? successUrl;
    } catch (err) {
      this.erroCompra.set((err as ApiError).message ?? this.i18n.t('creditosDialog.erroCompra'));
      this.comprando.set(false);
    }
  }

  protected verPacotes(): void {
    this.close();
    void this.router.navigate(['/checkout-tokens']);
  }

  protected aumentarPlano(): void {
    this.close();
    void this.router.navigate(['/checkout-planos']);
  }

  private async carregar(): Promise<void> {
    if (this.carregando()) return;
    this.carregando.set(true);
    try {
      // Saldo e catálogo em paralelo; o saldo pode estar velho depois do 402.
      const [, lista] = await Promise.all([
        this.creditos.recarregar(),
        this.servicos.listar().catch(() => [] as Servico[]),
      ]);
      this.pacotes.set(lista.filter((s) => s.tipo === 'AVULSO' && s.valor > 0));
      this.custos.set(lista.filter((s) => s.debitoToken > 0));
    } finally {
      this.carregando.set(false);
    }
  }
}
